// ── Explain Like My Teacher ──────────────────────────────────────────────────
// V2.1 bubble-progression explain section. Rendered INLINE in the slide's
// scroll container — not a modal. Reveals below the main point, fills the
// viewport, and auto-scrolls as new bubbles appear.
//
// Backward compat:
// - v2.1: explainEn is string[] → bubble progression with Got-it taps.
// - v2.0: explainEn is string   → single-bubble fallback, no progression.
// - v1:   explainEn empty/null  → parent decides not to render this at all.
//
// State is fully local to this component except for a small handshake with the
// parent:
//   onRevealBubble(index, total)        — parent tracks currentBubbleIndex
//   onAllBubblesRevealed()              — parent unlocks Next
//   onSkip()                            — parent unlocks Next, collapses section
//   onBackToSlide()                     — parent collapses and scrolls back
//
// The parent is expected to reset this component's key on slide change so
// its internal state (bubble index, local language) resets cleanly.

import { useEffect, useRef, useState } from "react";
// SOUND-APPLIED: tap click on every bubble advance / toggle / skip / back.
// topic_complete mini-chime once, when the last bubble is revealed.
import soundManager from "../sounds/sound-manager";

/**
 * Props:
 *   explainEn     : string | string[] | null
 *   explainHi     : string | string[] | null
 *   initialLang   : "en" | "hi" (local to this section, does not modify app lang)
 *   onAllBubblesRevealed : fn() — called once when the last bubble is shown
 *   onSkip               : fn() — called when "I get it, skip" is tapped
 *   onBackToSlide        : fn() — called when the top "↑ Back to slide" or
 *                          the final "↑ Back to slide" CTA is tapped
 */
export function ExplainTeacherSection({
  explainEn,
  explainHi,
  initialLang = "en",
  onAllBubblesRevealed,
  onSkip,
  onBackToSlide,
}) {
  // Normalize to arrays for the internal engine — string inputs become single-bubble arrays.
  // Empty-string guards: treat [""] and "" as "nothing to show".
  const toArr = (v) => {
    if (Array.isArray(v)) return v.filter(s => typeof s === "string" && s.trim().length > 0);
    if (typeof v === "string" && v.trim().length > 0) return [v];
    return [];
  };
  const bubblesEn = toArr(explainEn);
  const bubblesHi = toArr(explainHi);

  const [selectedLang, setSelectedLang] = useState(
    initialLang === "hi" && bubblesHi.length > 0 ? "hi" : (bubblesEn.length > 0 ? "en" : "hi")
  );

  // Active bubble source — falls back to the other language if one is missing
  const activeBubbles = selectedLang === "hi"
    ? (bubblesHi.length > 0 ? bubblesHi : bubblesEn)
    : (bubblesEn.length > 0 ? bubblesEn : bubblesHi);

  const total = activeBubbles.length;
  // Guard against zero — parent shouldn't have rendered us, but defend anyway
  const safeTotal = Math.max(total, 1);

  const [currentIndex, setCurrentIndex] = useState(0); // index of the LAST-shown bubble
  const allRevealed = currentIndex >= safeTotal - 1;

  // Fire completion callback exactly once when we reach the end
  const completedRef = useRef(false);
  useEffect(() => {
    if (allRevealed && !completedRef.current) {
      completedRef.current = true;
      onAllBubblesRevealed?.();
    }
  }, [allRevealed, onAllBubblesRevealed]);

  // Auto-scroll to the newest bubble when the list grows
  const listRef = useRef(null);
  useEffect(() => {
    if (!listRef.current) return;
    const t = setTimeout(() => {
      const last = listRef.current?.lastElementChild;
      if (last) last.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 150);
    return () => clearTimeout(t);
  }, [currentIndex]);

  const handleGotIt = () => {
    if (allRevealed) {
      // Final tap on the now-green "Back to slide" button → exit the section
      soundManager.play("tap");
      onBackToSlide?.();
      return;
    }
    // Advancing to another bubble — tap click for feedback; if this advance
    // lands on the LAST bubble, layer a short topic_complete chime so the
    // student feels the "you finished the explanation" moment.
    const willBeLast = currentIndex + 1 >= safeTotal - 1;
    soundManager.play("tap");
    if (willBeLast) soundManager.play("topic_complete");
    setCurrentIndex(i => Math.min(i + 1, safeTotal - 1));
  };

  // Keyboard: Enter advances "Got it"
  const gotItBtnRef = useRef(null);
  const onGotItKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleGotIt();
    }
  };

  // Visible bubbles — slice from 0..currentIndex inclusive
  const visible = activeBubbles.slice(0, currentIndex + 1);

  const showLangToggle = bubblesEn.length > 0 && bubblesHi.length > 0;

  return (
    <section
      aria-label="Teacher-style explanation"
      style={{
        // "Second screen" feel without breaking parent flex-columns:
        // use min-height with a conservative fallback (600px) + content-driven growth.
        // Earlier 100vh on a flex-column child caused the main slide layout to collapse
        // on some chapters, producing a blank-looking screen. 600px + auto-grow keeps
        // the look while staying flex-safe.
        minHeight: 600,
        background: "#EDF6FA",
        padding: "20px 20px 40px",
        marginTop: 24,
        // Full-width inside the slide's scroll container — break out of the slide's padding
        marginLeft: -20,
        marginRight: -20,
        fontFamily: "Poppins, sans-serif",
        boxSizing: "border-box",
      }}
    >
      {/* Top bar: Back ← + Language toggle → */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 20, maxWidth: 520, margin: "0 auto 20px",
      }}>
        <button
          type="button"
          aria-label="Back to slide"
          onClick={() => { soundManager.play("tap"); onBackToSlide?.(); }}
          style={{
            minHeight: 44, minWidth: 44,
            background: "transparent", border: "none",
            color: "#185FA5", fontSize: 13, fontWeight: 500,
            padding: "6px 8px", cursor: "pointer",
            fontFamily: "Poppins, sans-serif",
            display: "inline-flex", alignItems: "center", gap: 4,
          }}
        >
          ↑ Back to slide
        </button>

        {showLangToggle && (
          <div role="group" aria-label="Language toggle" style={{
            background: "#FFFFFF", borderRadius: 20, padding: 3,
            display: "flex", gap: 2,
          }}>
            <button
              type="button"
              aria-pressed={selectedLang === "en" ? "true" : "false"}
              onClick={() => { soundManager.play("tap"); setSelectedLang("en"); }}
              style={{
                padding: "4px 10px", borderRadius: 16,
                fontSize: 11, fontWeight: 500,
                border: "none", cursor: "pointer",
                fontFamily: "Poppins, sans-serif",
                background: selectedLang === "en" ? "#6AAAC8" : "transparent",
                color: selectedLang === "en" ? "#FFFFFF" : "#888780",
                minHeight: 28,
              }}
            >
              English
            </button>
            <button
              type="button"
              aria-pressed={selectedLang === "hi" ? "true" : "false"}
              onClick={() => { soundManager.play("tap"); setSelectedLang("hi"); }}
              style={{
                padding: "4px 10px", borderRadius: 16,
                fontSize: 11, fontWeight: 500,
                border: "none", cursor: "pointer",
                fontFamily: "Poppins, sans-serif",
                background: selectedLang === "hi" ? "#6AAAC8" : "transparent",
                color: selectedLang === "hi" ? "#FFFFFF" : "#888780",
                minHeight: 28,
              }}
            >
              Hinglish
            </button>
          </div>
        )}
      </div>

      {/* Section label + Skip */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 16, maxWidth: 520, margin: "0 auto 16px",
      }}>
        <div style={{
          fontSize: 12, color: "#5F5E5A", fontWeight: 500,
          letterSpacing: "0.3px", textTransform: "uppercase",
        }}>
          Explained like your teacher
        </div>
        <button
          type="button"
          aria-label="Skip explanation"
          onClick={() => { soundManager.play("tap"); onSkip?.(); }}
          style={{
            background: "transparent", border: "none",
            color: "#888780", fontSize: 12, cursor: "pointer",
            padding: "6px 4px", textDecoration: "none",
            fontFamily: "Poppins, sans-serif", minHeight: 28,
          }}
          onMouseOver={e => { e.currentTarget.style.textDecoration = "underline"; }}
          onMouseOut={e => { e.currentTarget.style.textDecoration = "none"; }}
        >
          I get it, skip
        </button>
      </div>

      {/* Bubble stack — aria-live so screen readers announce new bubbles */}
      <div
        ref={listRef}
        aria-live="polite"
        style={{
          display: "flex", flexDirection: "column", gap: 12,
          maxWidth: 520, margin: "0 auto",
        }}
      >
        {visible.map((text, i) => (
          <div
            key={`${selectedLang}-${i}`}
            style={{
              background: "#FFFFFF",
              borderLeft: "3px solid #6AAAC8",
              borderRadius: 8,
              borderTopLeftRadius: 0,
              padding: "14px 16px",
              fontSize: 15, lineHeight: 1.55,
              color: "#2C2C2A",
              fontFamily: "Poppins, sans-serif",
              animation: "explainBubbleFade 250ms ease-out both",
            }}
          >
            {text}
          </div>
        ))}
      </div>

      {/* Got it / Back to slide */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: 20, maxWidth: 520, margin: "20px auto 0" }}>
        <button
          ref={gotItBtnRef}
          type="button"
          aria-label={allRevealed ? "Back to slide" : "Next bubble"}
          onClick={handleGotIt}
          onKeyDown={onGotItKey}
          style={{
            minHeight: 44,
            padding: "10px 18px",
            background: allRevealed ? "#1A7A52" : "#6AAAC8",
            borderRadius: 24,
            color: "#FFFFFF",
            fontSize: 13, fontWeight: 500,
            border: "none", cursor: "pointer",
            fontFamily: "Poppins, sans-serif",
            transition: "background 0.2s ease",
          }}
        >
          {allRevealed ? "↑ Back to slide" : "Got it →"}
        </button>
      </div>

      {/* Progress hint: X / Y */}
      {total > 1 && (
        <div style={{
          textAlign: "center", marginTop: 12,
          fontSize: 11, color: "#888780",
          fontFamily: "Poppins, sans-serif",
        }}>
          {currentIndex + 1} / {total}
        </div>
      )}

      {/* Keyframe — scoped per instance */}
      <style>{`
        @keyframes explainBubbleFade {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
